// ==============================|| DESIGN TOKENS ||============================== //
// Single source of truth for every colour in the app.
// Components must never hard-code hex values: read them from the theme instead,
// e.g. sx={{ color: 'text.secondary' }} or theme.palette.custom.warnSoft.
//
// Palettes: 'iris' (indigo), 'pine' (teal), 'mantine' (Mantine default colours).
// Each user can pick one in the profile menu. ACTIVE_PALETTE is the default for users who haven't.

export const ACTIVE_PALETTE = 'mantine';

// choices shown in the profile menu (swatch = the colour shown next to the name)
export const PALETTE_CHOICES = [
  { id: 'iris', label: 'Violetinė', swatch: '#4f46e5' },
  { id: 'pine', label: 'Žalsva', swatch: '#0f766e' },
  { id: 'mantine', label: 'Mėlyna', swatch: '#228be6' }
];
export const PALETTE_STORAGE_KEY = 'ui.palette';

export const fonts = {
  sans: `'Figtree Variable', 'Figtree', system-ui, -apple-system, 'Segoe UI', sans-serif`,
  mono: `'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace`
};

const palettes = {
  iris: {
    light: {
      bg: '#f3f3f6',
      canvas: '#fafafc',
      panel: '#ffffff',
      hover: '#e9e9ef',
      border: '#e4e4eb',
      text: '#17171f',
      text2: '#4d4d5a',
      text3: '#6a6a78',
      primary: '#4f46e5',
      primaryHover: '#4338ca',
      onPrimary: '#ffffff',
      primarySoft: '#ececfe',
      primaryText: '#3a32c4',
      primary200: '#c7c9fb',
      primary800: '#2e2799',
      sel: '#f0f0fe',
      selRing: '#cdd0fb',
      logoBg: '#ffffff'
    },
    dark: {
      bg: '#0b0b0f',
      canvas: '#111117',
      panel: '#16161d',
      hover: '#1f1f28',
      border: '#272732',
      text: '#ececf2',
      text2: '#b1b1bf',
      text3: '#8b8b9a',
      primary: '#8f8ff9',
      primaryHover: '#a5a6fb',
      onPrimary: '#0d0d22',
      primarySoft: '#23233f',
      primaryText: '#b0b1ff',
      primary200: '#3b3b6b',
      primary800: '#c9caff',
      sel: '#1e1e34',
      selRing: '#3b3b6b',
      logoBg: '#1d1d26'
    }
  },
  // Mantine default palette (open-color). Neutral greys, blue primary.
  // Filled buttons use blue-8 (white text needs it to be readable); accents use the same family.
  mantine: {
    light: {
      bg: '#f1f3f5', // gray-1
      canvas: '#f8f9fa', // gray-0
      panel: '#ffffff',
      hover: '#e9ecef', // gray-2
      border: '#dee2e6', // gray-3
      text: '#212529', // gray-9
      text2: '#495057', // gray-7
      text3: '#6a727a', // gray-6, darkened to stay readable
      primary: '#1971c2', // blue-8
      primaryHover: '#1864ab', // blue-9
      onPrimary: '#ffffff',
      primarySoft: '#e7f5ff', // blue-0
      primaryText: '#1864ab', // blue-9
      primary200: '#a5d8ff', // blue-2
      primary800: '#1864ab',
      sel: '#e7f5ff',
      selRing: '#a5d8ff',
      logoBg: '#ffffff'
    },
    dark: {
      bg: '#1f1f1f', // dark-8
      canvas: '#242424', // dark-7
      panel: '#2e2e2e', // dark-6
      hover: '#3b3b3b', // dark-5
      border: '#424242', // dark-4
      text: '#e6e6e6',
      text2: '#c9c9c9', // dark-0
      text3: '#969696',
      primary: '#1971c2', // blue-8
      primaryHover: '#1c7ed6', // blue-7
      onPrimary: '#ffffff',
      primarySoft: '#293d4f',
      primaryText: '#4dabf7', // blue-4
      primary200: '#245078',
      primary800: '#a5d8ff',
      sel: '#2b3946',
      selRing: '#245078',
      logoBg: '#262626'
    }
  },
  pine: {
    light: {
      bg: '#f1f4f3',
      canvas: '#f9fbfa',
      panel: '#ffffff',
      hover: '#e6ebe9',
      border: '#e0e6e3',
      text: '#121916',
      text2: '#485550',
      text3: '#61706a',
      primary: '#0f766e',
      primaryHover: '#0d665f',
      onPrimary: '#ffffff',
      primarySoft: '#e3f3ef',
      primaryText: '#0b5e57',
      primary200: '#9fd3c8',
      primary800: '#094a44',
      sel: '#e9f5f2',
      selRing: '#b3dbd2',
      logoBg: '#ffffff'
    },
    dark: {
      bg: '#0a0e0d',
      canvas: '#0f1513',
      panel: '#141b19',
      hover: '#1c2522',
      border: '#25302c',
      text: '#e7eeeb',
      text2: '#a8b9b2',
      text3: '#84958e',
      primary: '#4fd1b5',
      primaryHover: '#6fdcc4',
      onPrimary: '#03201a',
      primarySoft: '#15302a',
      primaryText: '#74e2c9',
      primary200: '#23574b',
      primary800: '#9eeedb',
      sel: '#15302a',
      selRing: '#2a5a4f',
      logoBg: '#1a2320'
    }
  }
};

// Same for both palettes. Orange (LITNET brand) is reserved for "waiting on you".
const semantic = {
  light: {
    warn: '#d97b1a',
    onWarn: '#1f1408',
    warnSoft: '#fff6ea',
    warnBorder: '#f6dcb8',
    warnText: '#9a4d06',
    danger: '#b3321f',
    onDanger: '#ffffff',
    dangerSoft: '#fce8e5',
    dangerBorder: '#f3c4bb',
    dangerText: '#a3321f',
    success: '#1f8a4c',
    onSuccess: '#ffffff',
    successSoft: '#e5f4ea',
    successBorder: '#bfe3cb',
    successText: '#1b6a3a',
    info: '#1f6fb8',
    onInfo: '#ffffff',
    infoSoft: '#e6f0fb',
    infoBorder: '#c3d9f2',
    infoText: '#1f5a8f'
  },
  dark: {
    warn: '#f0a04b',
    onWarn: '#1f1408',
    warnSoft: '#2a1f12',
    warnBorder: '#4a3519',
    warnText: '#f3b56b',
    danger: '#f08a78',
    onDanger: '#1a0a07',
    dangerSoft: '#3a1914',
    dangerBorder: '#5a2a22',
    dangerText: '#f29b8b',
    success: '#6cc591',
    onSuccess: '#06180d',
    successSoft: '#14301e',
    successBorder: '#24503a',
    successText: '#80d3a1',
    info: '#8ec2f0',
    onInfo: '#07121c',
    infoSoft: '#172a3d',
    infoBorder: '#274763',
    infoText: '#b9dafa'
  }
};

// Status chips: background / text.
const status = {
  light: {
    pending: { bg: '#fdf1e3', fg: '#8a4a06' },
    approved: { bg: '#e5f4ea', fg: '#1b6a3a' },
    rejected: { bg: '#fce8e5', fg: '#a3321f' },
    new: { bg: '#efeff4', fg: '#4d4d5a' },
    planned: { bg: '#e6f0fb', fg: '#1f5a8f' },
    stopped: { bg: '#fdebe0', fg: '#a13d0c' }
  },
  dark: {
    pending: { bg: '#33240f', fg: '#f3b56b' },
    approved: { bg: '#14301e', fg: '#80d3a1' },
    rejected: { bg: '#3a1914', fg: '#f29b8b' },
    new: { bg: '#2a2a35', fg: '#c4c4d0' },
    planned: { bg: '#172a3d', fg: '#8ec2f0' },
    stopped: { bg: '#3a2414', fg: '#f5a46b' }
  }
};

const shadows = {
  light: {
    sm: '0 1px 2px rgba(20,20,40,.05)',
    lg: '0 1px 3px rgba(20,20,40,.05), 0 8px 24px rgba(20,20,40,.05)',
    active: '0 1px 2px rgba(20,20,40,.08), 0 0 0 1px rgba(20,20,40,.05)',
    popup: '0 4px 12px rgba(20,20,40,.08), 0 16px 40px rgba(20,20,40,.10)'
  },
  dark: {
    sm: 'none',
    lg: '0 0 0 1px rgba(0,0,0,.4)',
    active: '0 0 0 1px rgba(255,255,255,.06)',
    popup: '0 8px 32px rgba(0,0,0,.55)'
  }
};

// Neutral scale for legacy palette.grey / palette.greyNumb (light → dark in light mode,
// dark → light in dark mode, so "grey[50] background" still reads as a quiet surface).
const greyScale = {
  light: { 50: '#f7f7fa', 100: '#efeff4', 200: '#e4e4eb', 300: '#d4d4dd', 400: '#a3a3b0', 500: '#6a6a78', 600: '#5a5a68', 700: '#4d4d5a', 800: '#2a2a35', 900: '#17171f' },
  dark: { 50: '#16161d', 100: '#1f1f28', 200: '#272732', 300: '#34343f', 400: '#5c5c6b', 500: '#8b8b9a', 600: '#a3a3b0', 700: '#b1b1bf', 800: '#d4d4dd', 900: '#ececf2' }
};

// Icon colours for organization logo placeholders (picked by a hash of the name).
const logoColors = {
  light: ['#3a32c4', '#0b5e57', '#9a4d06', '#9b2360', '#1f5a8f'],
  dark: ['#b0b1ff', '#74e2c9', '#f3b56b', '#f29bc4', '#8ec2f0']
};

// Palette-specific replacements for the shared values above.
// (NEW gray, PLANNED blue, IN_PROGRESS yellow, DONE green, STOPPED orange, REJECTED red).
// In this app: pending = in progress (yellow), approved = done (green), rejected = red.
const paletteOverrides = {
  mantine: {
    light: {
      semantic: {
        warn: '#fab005', // yellow-6
        onWarn: '#1f1408',
        warnSoft: '#fff9db', // yellow-0
        warnBorder: '#ffec99', // yellow-2
        warnText: '#ae5a00',
        danger: '#e03131', // red-8
        onDanger: '#ffffff',
        dangerSoft: '#fff5f5', // red-0
        dangerBorder: '#ffc9c9', // red-2
        dangerText: '#c92a2a', // red-9
        success: '#2b8a3e', // green-9
        onSuccess: '#ffffff',
        successSoft: '#ebfbee', // green-0
        successBorder: '#b2f2bb', // green-2
        successText: '#277e39',
        info: '#1971c2', // blue-8
        onInfo: '#ffffff',
        infoSoft: '#e7f5ff',
        infoBorder: '#a5d8ff',
        infoText: '#1864ab'
      },
      status: {
        pending: { bg: '#fff9db', fg: '#ae5a00' }, // yellow
        approved: { bg: '#ebfbee', fg: '#277e39' }, // green
        rejected: { bg: '#fff5f5', fg: '#c92a2a' }, // red
        new: { bg: '#f1f3f5', fg: '#495057' }, // gray
        planned: { bg: '#e7f5ff', fg: '#1864ab' }, // blue
        stopped: { bg: '#fff4e6', fg: '#c6420e' } // orange
      },
      greyScale: { 50: '#f8f9fa', 100: '#f1f3f5', 200: '#e9ecef', 300: '#dee2e6', 400: '#ced4da', 500: '#adb5bd', 600: '#868e96', 700: '#495057', 800: '#343a40', 900: '#212529' },
      logoColors: ['#1971c2', '#2b8a3e', '#6741d9', '#d9480f', '#c2255c']
    },
    dark: {
      semantic: {
        warn: '#f08c00', // yellow-8
        onWarn: '#1f1408',
        warnSoft: '#493b28',
        warnBorder: '#724f1e',
        warnText: '#ffd43b', // yellow-4
        danger: '#ff6b6b', // red-5
        onDanger: '#1a0505',
        dangerSoft: '#522f2f',
        dangerBorder: '#7a3838',
        dangerText: '#ffa8a8', // red-3
        success: '#51cf66', // green-5
        onSuccess: '#06180d',
        successSoft: '#2e4432',
        successBorder: '#3d6b47',
        successText: '#8ce99a', // green-3
        info: '#4dabf7', // blue-4
        onInfo: '#07121c',
        infoSoft: '#2a3b4c',
        infoBorder: '#245078',
        infoText: '#a5d8ff'
      },
      status: {
        pending: { bg: '#554125', fg: '#ffd43b' },
        approved: { bg: '#2e4432', fg: '#8ce99a' },
        rejected: { bg: '#522f2f', fg: '#ffa8a8' },
        new: { bg: '#404143', fg: '#ced4da' },
        planned: { bg: '#2a3b4c', fg: '#74c0fc' },
        stopped: { bg: '#533727', fg: '#ffc078' }
      },
      greyScale: { 50: '#2e2e2e', 100: '#3b3b3b', 200: '#424242', 300: '#4a4a4a', 400: '#696969', 500: '#828282', 600: '#969696', 700: '#b8b8b8', 800: '#c9c9c9', 900: '#e6e6e6' },
      logoColors: ['#74c0fc', '#8ce99a', '#b197fc', '#ffc078', '#faa2c1']
    }
  }
};

export function getTokens(mode = 'light', paletteName = ACTIVE_PALETTE) {
  const m = mode === 'dark' ? 'dark' : 'light';
  const base = (palettes[paletteName] || palettes.iris)[m];
  const o = paletteOverrides[paletteName]?.[m] || {};
  return {
    mode: m,
    ...base,
    ...semantic[m],
    ...(o.semantic || {}),
    status: { ...status[m], ...(o.status || {}) },
    shadows: shadows[m],
    greyScale: o.greyScale || greyScale[m],
    logoColors: o.logoColors || logoColors[m],
    fonts
  };
}